import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Leaf, Recycle, Award, ArrowRight, Cloud, MapPin, Trash2, Lightbulb, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageContent } from '../hooks/usePageContent';

const IMAGES = {
  forest: "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1920&q=80",
};

const PHOTOS = [
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/99fiezuz_SOPREL_V1_51310_0127.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/l6v8xoqj_SOPREL_V1_51310_0128.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/1dyktg8o_SOPREL_V1_51310_0131.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/el4kbwth_SOPREL_V1_77130_0054.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/eilnxfcg_SOPREL_V1_77130_0070.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/6r7w1fi9_SOPREL_V1_51310_0126.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/j7u2boo6_SOPREL_V1_51310_0111.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/37enyx1x_SOPREL_V1_51310_0110.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/zgq6hlrh_SOPREL_V1_51310_0097.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/kq25xfxy_SOPREL_V1_51310_0095.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/mfajgbpt_SOPREL_V1_27600_0055.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/ehu86bsv_SOPREL_V1_27600_0013.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/ix5kt9eg_SOPREL_V1_27600_0001.JPG",
];

const Anim = ({ children, className = '', delay = 0 }) => {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const ENG_DEFAULTS = {
  hero_title: 'Nos Engagements',
  hero_subtitle: "Chez SOPREL Groupe, nous plaçons l'humain, la sécurité, la qualité et le développement durable au cœur de notre stratégie industrielle.",
  expertise_label: 'Savoir-faire',
  expertise_title: 'Notre expertise à votre service',
  expertise_text1: "fort de 70 ans d'expérience dans la préfabrication béton, SOPREL Groupe maîtrise l'ensemble de la chaîne de production. De la conception à la livraison, chaque étape est rigoureusement contrôlée pour garantir des produits de qualité supérieure.",
  expertise_text2: "Nos équipes d'ingénieurs et de techniciens qualifiés travaillent en étroite collaboration avec nos clients pour développer les solutions les plus adaptées à chaque projet.",
  quality_title: 'Qualité et Sécurité',
  quality_text: "La qualité et la sécurité sont les fondements de notre activité industrielle. Chaque produit est soumis à des contrôles rigoureux tout au long du processus de fabrication.",
  carbon_title_l1: 'DIMINUER',
  carbon_title_l2: "L'EMPREINTE",
  carbon_title_l3: 'CARBONE',
  values_title: 'Partageons nos valeurs',
  values_text: "Vous souhaitez en savoir plus sur nos engagements ou discuter d'un projet ? Contactez-nous.",
};

const EngagementsPage = () => {
  const { content: c } = usePageContent('engagements', ENG_DEFAULTS);
  return (
  <div data-testid="engagements-page">

    {/* ── HEADER ── */}
    <section className="pt-32 pb-20 bg-[#0e1c3f]" data-testid="engagements-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Notre Responsabilité</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">{c.hero_title}</h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            {c.hero_subtitle}
          </p>
        </div>
      </div>
    </section>

    {/* ── 1. INNOVATIONS ── */}
    <section className="py-24 bg-white" data-testid="innovations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Titre + intro */}
        <Anim>
          <div className="max-w-3xl mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0078b7] rounded-sm flex items-center justify-center">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm">Innovations</p>
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-6">
              {c.expertise_title}
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              <strong className="text-[#0e1c3f]">GROUPE SOPREL</strong>, {c.expertise_text1}
            </p>
          </div>
        </Anim>

        {/* Grande image + texte côte à côte */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-20">
          <Anim>
            <div className="relative">
              <div className="aspect-[3/3] overflow-hidden rounded-sm">
                <img src="https://customer-assets.emergentagent.com/job_soprel-build/artifacts/mcfzovh4_SOPREL_V1_27600_0054%20%281%29.JPG" alt="Usine SOPREL" className="w-full h-full object-cover" />
              </div>
            </div>
          </Anim>
          <Anim delay={150}>
            <div className="space-y-6 text-slate-600 leading-relaxed">
              <p>
                Depuis sa création, GROUPE SOPREL développe une vision pionnière de la conception technique en plaçant l'innovation au cœur de son évolution. Précurseur dans l'élaboration de solutions performantes et sur mesure, le groupe mobilise son savoir-faire pour répondre avec précision aux enjeux les plus exigeants, tout en privilégiant une approche fondée sur l'écoute, la proximité et la compréhension des attentes de chacun de ses clients.
              </p>
              <p>
                Cette quête permanente d'excellence s'accompagne naturellement d'un engagement en faveur de solutions durables, intégrant les nouvelles technologies à fort intérêt environnemental. Soucieux de bâtir des relations de confiance pérennes, GROUPE SOPREL associe expertise, bienveillance et accompagnement personnalisé afin de proposer à chaque projet une réponse à la fois innovante, responsable et humaine. Ainsi, le groupe se positionne comme un partenaire stratégique, conjuguant innovation, performance et responsabilité au service de vos projets.
              </p>
            </div>
          </Anim>
        </div>

        {/* Bandeau expertise bleu */}
        <Anim>
          <div className="bg-[#0e1c3f] p-6 sm:p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#0078b7] rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <h3 className="font-heading font-bold text-2xl text-white mb-4">
                Une Expertise Solide au Service de l'Industrie du Béton
              </h3>
              <p className="text-white/70 leading-relaxed max-w-3xl">
                La force du GROUPE SOPREL réside avant tout dans les femmes et les hommes qui composent l'entreprise. Fort de plusieurs décennies d'expérience dans le domaine du béton préfabriqué et de la construction, le groupe s'appuie sur des équipes passionnées, qualifiées et profondément investies, dont le savoir-faire constitue le socle de son excellence.
              </p>
            </div>
          </div>
        </Anim>
      </div>
    </section>

    {/* ── 2. ENGAGEMENT QUALITÉ ── */}
    <section className="py-24 bg-slate-50" data-testid="engagement-qualite-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Texte */}
          <Anim>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#ff65c3] rounded-sm flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <p className="text-[#ff65c3] font-bold tracking-[0.2em] uppercase text-sm">Engagement</p>
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-6">
                {c.quality_title}
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg mb-8">
                {c.quality_text}
              </p>
            </div>
          </Anim>

          {/* Grille photos 2x2 */}
          <Anim delay={200}>
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-[3/4] overflow-hidden rounded-sm group">
                <img src={PHOTOS[0]} alt="Contrôle qualité" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm group">
                <img src={PHOTOS[6]} alt="Production" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm group">
                <img src={PHOTOS[8]} alt="Armatures" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm group">
                <img src={PHOTOS[12]} alt="Usine" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
          </Anim>
        </div>
      </div>
    </section>

    {/* ── 3. EMPREINTE CARBONE ── */}
    <section className="relative min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] overflow-hidden" data-testid="carbon-footprint-section">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.forest})` }} />
      <div className="absolute inset-0 bg-[#0e1c3f]/75" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          <div>
            <p className="text-white/80 font-bold tracking-[0.2em] uppercase text-sm mb-6 border-b border-white/30 pb-2 inline-block">Développement durable</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8 leading-tight">
              {c.carbon_title_l1}<br />{c.carbon_title_l2}<br /><span className="text-[#ff65c3]">{c.carbon_title_l3}</span>
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-10">
              Depuis 1957, GROUPE SOPREL fait de l'évolution et de l'innovation les moteurs de sa croissance. En anticipant les mutations du marché de la construction et en intégrant des technologies de pointe, le groupe a su s'imposer comme un acteur de référence. Aujourd'hui, il poursuit cette dynamique à travers une démarche résolument tournée vers le développement durable.
            </p>
            <Button asChild className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0e1c3f] rounded-sm px-8">
              <Link to="/contact">Nous contacter <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Cloud, title: 'CIMENT À FAIBLE ÉMISSION DE CO2', text: 'Réduction des émissions de dioxyde de carbone.' },
              { icon: MapPin, title: 'CIRCUITS COURTS', text: 'Approvisionnement local, fournisseurs dans un rayon de moins de 50 km.' },
              { icon: Recycle, title: 'PRÉFABRICATION INDUSTRIELLE', text: 'Une gestion rigoureuse et un contrôle précis des matières premières' },
              { icon: Trash2, title: 'RÉDUCTION DES DÉCHETS', text: 'Contrôle qualité garantissant la livraison de produits sans déchet.' },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <Anim key={i} delay={i * 150}>
                  <div className="group bg-[#0078b7] p-6 flex flex-col justify-between min-h-[220px] transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#0078b7]/30">
                    <div>
                      <h3 className="text-[#ff65c3] font-bold text-sm tracking-wide mb-3 leading-tight group-hover:text-white transition-colors">{c.title}</h3>
                      <p className="text-white/90 text-sm leading-relaxed">{c.text}</p>
                    </div>
                    <Icon className="h-10 w-10 text-[#ff65c3] mt-4 group-hover:text-white transition-all duration-500 group-hover:scale-110" strokeWidth={1.5} />
                  </div>
                </Anim>
              );
            })}
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA removed ── */}
  </div>
  );
};

export default EngagementsPage;
